import {
  accumulate,
  distributeAnnual,
  KPI_AGGREGATION_LABELS,
  TARGET_PERIOD_COUNT,
  TARGET_PERIOD_TYPE_LABELS,
  TARGET_PERIOD_TYPES,
  formatDateTime,
  TARGET_APPROVAL_LABELS,
  targetBatchResultSchema,
  targetCopyResultSchema,
  targetDecisionResultSchema,
  targetPendingListSchema,
  targetSettingsSchema,
  targetGridSchema,
  targetHistorySchema,
  targetOptionsSchema,
  targetClosingMonth,
  targetPeriodLabel,
  targetValueSchema,
  type DistributionModeName,
  type TargetApprovalStatusName,
  type TargetGrid,
  type TargetPending,
  type TargetKpi,
  type TargetPeriodTypeName,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Copy, FolderKanban, Flag, History, Lock, ShieldCheck, Split } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { AuditLink } from '@/features/audit/AuditLink';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors } from '@/lib/forms';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
/**
 * Lê o valor digitado no padrão brasileiro: vírgula decimal ("1.234,5") e ponto de milhar só quando o
 * agrupamento bate ("2.000.000"); sem vírgula e fora desse padrão, o ponto vale como decimal ("80.5").
 */
export function parseValue(raw: string): number | null | 'invalid' {
  const t = raw.trim().replace(/\s/g, '');
  if (t === '') return null;
  const thousands = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(t);
  const normalized = t.includes(',') || thousands ? t.replace(/\./g, '').replace(',', '.') : t;
  const parsed = targetValueSchema.safeParse(normalized);
  return parsed.success ? parsed.data : 'invalid';
}
const cellKeyOf = (kpiId: string, period: number) => `${kpiId}|${period}`;
type SavedCell = {
  value: number | null;
  version: number;
  approvalStatus: TargetApprovalStatusName;
  approvedValue: number | null;
};

/** Indicadores › Metas (T-062, RF-23, PRD §11.12): grade de metas do ano, por tipo e recorte. */
export function MetasPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-[90rem]">
      <PageHeader
        title="Metas"
        description="As metas de cada indicador por período do ano — mensais, trimestrais, anuais ou acumuladas —, gerais ou por unidade e área. Revisar uma meta guarda a versão anterior."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="As metas pertencem a um projeto. Quando a empresa tiver um projeto em andamento, elas aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: as metas ficam disponíveis só para consulta.
            </p>
          ) : null}
          <TargetsSection
            organizationId={organizationId}
            projectId={project.id}
            defaultYear={Math.min(
              Math.max(new Date().getFullYear(), project.horizonStart),
              project.horizonEnd,
            )}
            readOnly={project.status !== 'ACTIVE'}
          />
        </div>
      )}
    </PageContainer>
  );
}

function TargetsSection({
  organizationId,
  projectId,
  defaultYear,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  defaultYear: number;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const user = useCurrentUser();
  const can = useCan();
  const canEdit = !readOnly && can('metas-resultados', 'edit');
  // Quem aprova metas: decide as pendências e é quem altera meta já aprovada (DEC-004 item 7).
  const canApprove = can('metas-resultados', 'approve');
  // Ligar/desligar a aprovação é customização da Fluxus por cliente (DEC-004 itens 8 e 20).
  const canConfigure = user.organization.isFluxus && can('administracao', 'edit');
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/targets`;
  const [year, setYear] = useState(defaultYear);
  const [periodType, setPeriodType] = useState<TargetPeriodTypeName>('MONTH');
  // Recorte: "" = geral; "u:<id>" = unidade; "d:<id>" = área.
  const [slice, setSlice] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [askReason, setAskReason] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [distributing, setDistributing] = useState<TargetKpi | null>(null);
  const [copying, setCopying] = useState(false);
  const [historyOf, setHistoryOf] = useState<{ kpi: TargetKpi; period: number } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const ids = { year: useId(), type: useId(), slice: useId() };
  const businessUnitId = slice.startsWith('u:') ? slice.slice(2) : undefined;
  const departmentId = slice.startsWith('d:') ? slice.slice(2) : undefined;

  const options = useQuery({
    queryKey: ['targets', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: targetOptionsSchema, signal }),
  });
  const settingsKey = ['target-settings', organizationId];
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/target-settings`, {
        schema: targetSettingsSchema,
        signal,
      }),
  });
  const approvalRequired = settings.data?.approvalRequired ?? false;
  const pending = useQuery({
    queryKey: ['targets', projectId, 'pending'],
    queryFn: ({ signal }) => client.request(`${base}/pending`, { schema: targetPendingListSchema, signal }),
    enabled: canApprove && !readOnly,
  });
  const toggleApproval = useMutation({
    mutationFn: (next: boolean) =>
      client.request(`/organizations/${organizationId}/target-settings`, {
        method: 'PUT',
        body: { approvalRequired: next },
        schema: targetSettingsSchema,
      }),
    meta: { handlesError: true },
    // O erro aparece na faixa da configuração.
    onError: () => setToggling(false),
    onSuccess: async (result) => {
      setToggling(false);
      toast.success(result.approvalRequired ? 'Aprovação de metas ligada' : 'Aprovação de metas desligada');
      await queryClient.invalidateQueries({ queryKey: settingsKey });
    },
  });
  const gridKey = ['targets', projectId, 'grid', year, periodType, slice];
  const grid = useQuery({
    queryKey: gridKey,
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { year: String(year), periodType, businessUnitId, departmentId },
        schema: targetGridSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  const periods = Array.from({ length: TARGET_PERIOD_COUNT[periodType] }, (_, i) => i + 1);
  const saved = useMemo(() => {
    // Inclui as metas removidas (value null): têm histórico, e definir de novo é revisão.
    const map = new Map<string, SavedCell>();
    for (const c of grid.data?.cells ?? []) map.set(cellKeyOf(c.kpiId, c.period), c);
    return map;
  }, [grid.data]);
  const dirtyKeys = Object.keys(drafts).filter((k) => {
    const parsed = parseValue(drafts[k]!);
    return parsed === 'invalid' || parsed !== (saved.get(k)?.value ?? null);
  });
  const invalid = dirtyKeys.some((k) => parseValue(drafts[k]!) === 'invalid');
  // Revisão = muda ou remove uma meta já gravada (pede justificativa — DEC-004 item 10).
  const revising = dirtyKeys.some((k) => saved.has(k));
  // Período encerrado (RN-06, T-066): meta que fecha num mês fechado pede justificativa, até a primeira.
  const closedMonths = grid.data?.closedMonths ?? [];
  const isClosedPeriod = (p: number) => closedMonths.includes(targetClosingMonth(periodType, p));
  const closedDirty = [...new Set(dirtyKeys.map((k) => Number(k.split('|')[1])).filter(isClosedPeriod))].sort(
    (a, b) => a - b,
  );
  const closedLabels = closedDirty.map((p) => `${targetPeriodLabel(periodType, p)}/${year}`);
  const needsReason = revising || closedDirty.length > 0;

  // Meta aprovada (com valor) fica só para leitura para quem não aprova metas (DEC-004 item 7).
  const isLocked = (key: string) => {
    const c = saved.get(key);
    return !canApprove && c?.approvalStatus === 'APPROVED' && c.value !== null;
  };
  const resetDrafts = () => {
    setDrafts({});
    setSaveError(null);
  };
  const [pendingView, setPendingView] = useState<(() => void) | null>(null);
  const changeView = (fn: () => void) => {
    if (dirtyKeys.length) return setPendingView(() => fn);
    resetDrafts();
    fn();
  };

  async function save(reason: string | null) {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await client.request(base, {
        method: 'PUT',
        body: {
          reason,
          cells: dirtyKeys.map((k) => {
            const [kpiId, period] = k.split('|') as [string, string];
            return {
              kpiId,
              periodType,
              year,
              period: Number(period),
              businessUnitId: businessUnitId ?? null,
              departmentId: departmentId ?? null,
              value: parseValue(drafts[k]!) as number | null,
            };
          }),
        },
        schema: targetBatchResultSchema,
      });
      setAskReason(false);
      // Os rascunhos só somem com a grade nova na tela (sem piscar os valores antigos).
      await queryClient.invalidateQueries({ queryKey: ['targets', projectId] });
      resetDrafts();
      const parts = [
        result.created ? `${result.created} nova${result.created === 1 ? '' : 's'}` : null,
        result.revised ? `${result.revised} revisada${result.revised === 1 ? '' : 's'}` : null,
        result.removed ? `${result.removed} removida${result.removed === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      toast.success(result.pendingApproval ? 'Metas enviadas para aprovação' : 'Metas salvas', {
        description: parts.join(', ') || undefined,
      });
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (err instanceof ApiError && err.code === 'CLOSED_PERIOD') {
        // Um mês fechou enquanto se editava: a grade é lida de novo (marca o mês) e a justificativa é pedida.
        setSaveError(new Error(err.message));
        setAskReason(true);
        void queryClient.invalidateQueries({ queryKey: gridKey });
      } else if (fields.reason && reason === null) {
        // 4. A tela achou que eram metas novas, mas alguém gravou nessas células enquanto se editava:
        // recarrega a grade (os rascunhos ficam) e pede a justificativa da revisão.
        setSaveError(
          new Error(
            'Algumas destas metas foram gravadas enquanto você editava: salvar agora é uma revisão e pede justificativa.',
          ),
        );
        setAskReason(true);
        void queryClient.invalidateQueries({ queryKey: gridKey });
      } else if (fields.reason) {
        setSaveError(new Error(err instanceof ApiError ? err.message : fields.reason));
      } else {
        setSaveError(err);
        setAskReason(false);
      }
    } finally {
      setSaving(false);
    }
  }

  const sliceOptions = options.data;
  const kpis = grid.data?.kpis ?? [];
  const sliceName = businessUnitId
    ? `a unidade ${sliceOptions?.businessUnits.find((u) => u.id === businessUnitId)?.name ?? ''}`
    : departmentId
      ? `a área ${sliceOptions?.departments.find((d) => d.id === departmentId)?.name ?? ''}`
      : 'a meta geral';

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano
          </label>
          <NativeSelect
            id={ids.year}
            value={String(year)}
            onChange={(e) => {
              // O valor é lido agora: na confirmação, o select controlado já voltou ao valor antigo.
              const next = Number(e.target.value);
              changeView(() => setYear(next));
            }}
          >
            {(sliceOptions?.years ?? [year]).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.type} className="mb-1 block text-xs font-medium text-muted-foreground">
            Tipo de meta
          </label>
          <NativeSelect
            id={ids.type}
            value={periodType}
            onChange={(e) => {
              const next = e.target.value as TargetPeriodTypeName;
              changeView(() => setPeriodType(next));
            }}
          >
            {TARGET_PERIOD_TYPES.map((t) => (
              <option key={t} value={t}>
                {TARGET_PERIOD_TYPE_LABELS[t]}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.slice} className="mb-1 block text-xs font-medium text-muted-foreground">
            Recorte
          </label>
          <NativeSelect
            id={ids.slice}
            value={slice}
            onChange={(e) => {
              const next = e.target.value;
              changeView(() => setSlice(next));
            }}
          >
            <option value="">Meta geral</option>
            {sliceOptions?.businessUnits.length ? (
              <optgroup label="Por unidade">
                {sliceOptions.businessUnits.map((u) => (
                  <option key={u.id} value={`u:${u.id}`}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {sliceOptions?.departments.length ? (
              <optgroup label="Por área">
                {sliceOptions.departments.map((d) => (
                  <option key={d.id} value={`d:${d.id}`}>
                    {d.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </NativeSelect>
        </div>
      </div>

      {settings.isError ? (
        <FormAlert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível saber se a aprovação de metas está ligada: {errorMessage(settings.error)}
            <Button
              variant="outline"
              size="sm"
              loading={settings.isFetching}
              onClick={() => void settings.refetch()}
            >
              Tentar de novo
            </Button>
          </span>
        </FormAlert>
      ) : settings.data ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>
              <span className="font-semibold">
                Aprovação de metas {approvalRequired ? 'ligada' : 'desligada'}.
              </span>{' '}
              <span className="text-muted-foreground">
                {approvalRequired
                  ? 'Meta nova ou revisada fica pendente e só vale depois de aprovada.'
                  : 'O que se grava já vale.'}{' '}
                Meta aprovada só é alterada por quem aprova metas.
              </span>
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {canApprove && !readOnly && (approvalRequired || !pending.data || pending.data.total > 0) ? (
              <Button variant="outline" size="sm" onClick={() => setReviewing(true)}>
                <CheckCheck aria-hidden />
                {pending.data
                  ? `Aprovações pendentes (${pending.data.total})`
                  : pending.isError
                    ? 'Aprovações pendentes (erro ao carregar)'
                    : 'Aprovações pendentes (…)'}
              </Button>
            ) : null}
            {canConfigure ? (
              <Button variant="outline" size="sm" onClick={() => setToggling(true)}>
                {approvalRequired ? 'Desligar a aprovação' : 'Ligar a aprovação'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {toggleApproval.isError ? (
        <FormAlert tone="error">{errorMessage(toggleApproval.error)}</FormAlert>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCopying(true)}
            disabled={!sliceOptions || dirtyKeys.length > 0}
          >
            <Copy aria-hidden />
            Copiar de outro ano
          </Button>
          <span className="text-xs text-muted-foreground">
            Digite nas células e salve tudo de uma vez. Mudar uma meta já gravada pede justificativa e guarda
            a anterior.
            {!canApprove ? ' Metas aprovadas ficam só para leitura.' : ''}
          </span>
        </div>
      ) : null}

      {closedMonths.length ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm"
        >
          <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            <span className="font-semibold">
              {closedMonths.length === 1 ? 'Mês fechado' : 'Meses fechados'} em {year}:{' '}
              {closedMonths.map((m) => targetPeriodLabel('MONTH', m)).join(', ')}.
            </span>{' '}
            Metas que fecham {closedMonths.length === 1 ? 'nele' : 'neles'} pedem justificativa e ficam
            registradas na auditoria como período encerrado.
          </span>
        </p>
      ) : null}
      {grid.isPending ? (
        <LoadingState rows={4} label="Carregando as metas" />
      ) : grid.isError ? (
        <ErrorState error={grid.error} onRetry={() => void grid.refetch()} retrying={grid.isFetching} />
      ) : kpis.length === 0 ? (
        user.areaScope === 'OWN' ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-12"
            icon={Flag}
            title="Nenhum indicador seu neste recorte"
            description="Você vê as metas dos indicadores das suas áreas e as desdobradas para as suas áreas ou unidades. Escolha outro recorte acima."
          />
        ) : (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-12"
            icon={Flag}
            title="Nenhum indicador para definir metas"
            description="Cadastre os indicadores do projeto em Indicadores › KPIs; eles aparecem aqui para receber metas."
            action={
              can('indicadores', 'view') ? (
                <Button asChild variant="outline">
                  <Link to={`/empresas/${organizationId}/indicadores/kpis`}>Ir para os KPIs</Link>
                </Button>
              ) : null
            }
          />
        )
      ) : (
        <TargetTable
          organizationId={organizationId}
          grid={grid.data}
          periods={periods}
          periodType={periodType}
          drafts={drafts}
          saved={saved}
          canEdit={canEdit}
          isLocked={isLocked}
          isClosedPeriod={isClosedPeriod}
          busy={saving || grid.isFetching}
          locked={saving}
          onChange={(key, value) => setDrafts((d) => ({ ...d, [key]: value }))}
          onDistribute={setDistributing}
          onHistory={(kpi, period) => setHistoryOf({ kpi, period })}
        />
      )}

      {canEdit && dirtyKeys.length ? (
        <div className="sticky bottom-3 z-10 flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-3 shadow-md sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm">
            {invalid
              ? 'Há células com valor inválido (use números).'
              : `${dirtyKeys.length} ${dirtyKeys.length === 1 ? 'meta alterada' : 'metas alteradas'}${closedDirty.length ? ` — inclui período fechado (${closedLabels.join(', ')}), pede justificativa` : revising ? ' — inclui revisão de meta já gravada' : ''}.`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetDrafts} disabled={saving}>
              Descartar
            </Button>
            <Button
              loading={saving && !askReason}
              disabled={invalid}
              onClick={() => (needsReason ? setAskReason(true) : void save(null))}
            >
              Salvar metas
            </Button>
          </div>
        </div>
      ) : null}
      {saveError && !askReason ? <FormAlert tone="error">{errorMessage(saveError)}</FormAlert> : null}

      <ConfirmDialog
        open={pendingView !== null}
        onOpenChange={(open) => !open && setPendingView(null)}
        title="Descartar as metas não salvas?"
        description="Há metas alteradas nesta grade que ainda não foram salvas. Trocar de ano, tipo ou recorte descarta essas alterações."
        confirmLabel="Descartar e trocar"
        destructive
        onConfirm={() => {
          resetDrafts();
          pendingView?.();
          setPendingView(null);
        }}
      />
      <Dialog
        open={askReason}
        onOpenChange={(o) => !o && setAskReason(false)}
        busy={saving}
        title={closedDirty.length ? 'Justificar: período fechado' : 'Justificar a revisão'}
      >
        {askReason ? (
          <ReasonForm
            closedLabels={closedLabels}
            saving={saving}
            error={saveError}
            onCancel={() => setAskReason(false)}
            onSubmit={(r) => void save(r)}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={distributing !== null}
        onOpenChange={(o) => !o && setDistributing(null)}
        title="Distribuir a meta anual nos meses"
      >
        {distributing ? (
          <DistributeForm
            client={client}
            base={base}
            projectId={projectId}
            kpi={distributing}
            year={year}
            businessUnitId={businessUnitId}
            departmentId={departmentId}
            onCancel={() => setDistributing(null)}
            onApply={(values) => {
              const skipped = values.filter((_, i) => isLocked(cellKeyOf(distributing.id, i + 1))).length;
              setDrafts((d) => {
                const next = { ...d };
                values.forEach((v, i) => {
                  const key = cellKeyOf(distributing.id, i + 1);
                  if (!isLocked(key)) next[key] = formatValue(v);
                });
                return next;
              });
              setDistributing(null);
              if (skipped) {
                toast.info(
                  `${skipped} ${skipped === 1 ? 'mês tem meta aprovada e ficou' : 'meses têm meta aprovada e ficaram'} como estava${skipped === 1 ? '' : 'm'}`,
                  { description: 'Meta aprovada só é alterada por quem aprova metas.' },
                );
              }
            }}
          />
        ) : null}
      </Dialog>
      <Dialog open={copying} onOpenChange={setCopying} title="Copiar metas de outro ano">
        {copying && sliceOptions ? (
          <CopyForm
            years={sliceOptions.years.filter((y) => y !== year)}
            toYear={year}
            periodType={periodType}
            sliceName={sliceName}
            onCancel={() => setCopying(false)}
            onCopy={async (fromYear, allTypes, allSlices) => {
              const result = await client.request(`${base}/copy`, {
                method: 'POST',
                body: {
                  fromYear,
                  toYear: year,
                  periodTypes: allTypes ? [...TARGET_PERIOD_TYPES] : [periodType],
                  breakdown: allSlices
                    ? undefined
                    : { businessUnitId: businessUnitId ?? null, departmentId: departmentId ?? null },
                },
                schema: targetCopyResultSchema,
              });
              setCopying(false);
              toast.success(
                result.copied
                  ? `${result.copied} ${result.copied === 1 ? 'meta copiada' : 'metas copiadas'}`
                  : 'Nada para copiar',
                {
                  description:
                    [
                      result.existing
                        ? `${result.existing} já ${result.existing === 1 ? 'tinha' : 'tinham'} meta em ${year} (mantida${result.existing === 1 ? '' : 's'}).`
                        : null,
                      result.removed
                        ? `${result.removed} com a meta removida em ${year} (${result.removed === 1 ? 'continua' : 'continuam'} sem meta).`
                        : null,
                      result.archived
                        ? `${result.archived} de unidade ou área arquivada (não copiada${result.archived === 1 ? '' : 's'}).`
                        : null,
                      result.closed
                        ? `${result.closed} de ${result.closed === 1 ? 'mês fechado' : 'meses fechados'} (não copiada${result.closed === 1 ? '' : 's'}: defina na grade, com justificativa).`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined,
                },
              );
              await queryClient.invalidateQueries({ queryKey: ['targets', projectId] });
            }}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={toggling}
        onOpenChange={(open) => !open && setToggling(false)}
        title={approvalRequired ? 'Desligar a aprovação de metas?' : 'Ligar a aprovação de metas?'}
        description={
          approvalRequired
            ? 'O que se gravar depois já vale. As metas que estão pendentes continuam esperando decisão.'
            : 'Meta nova ou revisada passa a ficar pendente e só vale depois de aprovada por quem aprova metas.'
        }
        confirmLabel={approvalRequired ? 'Desligar' : 'Ligar'}
        busy={toggleApproval.isPending}
        onConfirm={() => toggleApproval.mutate(!approvalRequired)}
      />
      <Dialog
        open={reviewing}
        onOpenChange={setReviewing}
        title="Aprovações pendentes"
        className="w-[min(48rem,calc(100vw-2rem))]"
      >
        {reviewing ? (
          <ApprovalPanel
            client={client}
            base={base}
            pending={pending}
            onDecided={() => queryClient.invalidateQueries({ queryKey: ['targets', projectId] })}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={historyOf !== null}
        onOpenChange={(o) => !o && setHistoryOf(null)}
        title="Histórico da meta"
      >
        {historyOf ? (
          <TargetHistory
            client={client}
            base={base}
            projectId={projectId}
            kpi={historyOf.kpi}
            periodType={periodType}
            year={year}
            period={historyOf.period}
            businessUnitId={businessUnitId}
            departmentId={departmentId}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function TargetTable({
  organizationId,
  grid,
  periods,
  periodType,
  drafts,
  saved,
  canEdit,
  isLocked,
  isClosedPeriod,
  busy,
  locked,
  onChange,
  onDistribute,
  onHistory,
}: {
  organizationId: string;
  grid: TargetGrid;
  periods: number[];
  periodType: TargetPeriodTypeName;
  drafts: Record<string, string>;
  saved: Map<string, SavedCell>;
  canEdit: boolean;
  isLocked: (key: string) => boolean;
  /** O período fecha num mês fechado (T-066). */
  isClosedPeriod: (period: number) => boolean;
  busy: boolean;
  /** Enquanto salva: o que se digitasse agora não iria no lote e se perderia. */
  locked: boolean;
  onChange: (key: string, value: string) => void;
  onDistribute: (kpi: TargetKpi) => void;
  onHistory: (kpi: TargetKpi, period: number) => void;
}) {
  const showTotal = periodType === 'MONTH' || periodType === 'QUARTER';
  return (
    <div
      role="region"
      aria-label="Grade de metas"
      tabIndex={0}
      className="overflow-x-auto rounded-lg border border-border"
    >
      <table className="w-full border-collapse text-sm" aria-busy={busy}>
        <caption className="sr-only">
          Metas {TARGET_PERIOD_TYPE_LABELS[periodType].toLowerCase()} de {grid.year}
        </caption>
        <thead className="bg-muted/60 text-xs text-muted-foreground">
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-[1] w-36 min-w-36 bg-muted sm:w-auto sm:min-w-56 px-3 py-2 text-left font-semibold"
            >
              Indicador
            </th>
            {periods.map((p) => (
              <th key={p} scope="col" className="min-w-28 px-2 py-2 text-right font-semibold">
                <span className="inline-flex items-center justify-end gap-1">
                  {isClosedPeriod(p) ? <Lock className="size-3" aria-hidden /> : null}
                  {targetPeriodLabel(periodType, p)}
                  {isClosedPeriod(p) ? <span className="sr-only">, fechado</span> : null}
                </span>
              </th>
            ))}
            {showTotal ? (
              <th scope="col" className="min-w-32 px-3 py-2 text-right font-semibold">
                Ano (acumulado)
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {grid.kpis.map((kpi) => {
            const values = periods.map((p) => {
              const key = cellKeyOf(kpi.id, p);
              const draft = drafts[key];
              if (draft !== undefined) {
                const parsed = parseValue(draft);
                return parsed === 'invalid' ? null : parsed;
              }
              return saved.get(key)?.value ?? null;
            });
            const total = accumulate(values, kpi.aggregation);
            return (
              <tr key={kpi.id} className="border-t border-border">
                <th
                  scope="row"
                  className="sticky left-0 z-[1] w-36 min-w-36 bg-surface px-3 py-2 text-left align-top font-normal sm:w-auto sm:min-w-56"
                >
                  <p className="font-semibold">{kpi.name}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-mono">{kpi.code}</span> · {kpi.unitLabel} · {kpi.perspective.name}
                  </p>
                  {canEdit && periodType === 'MONTH' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-2 mt-1 h-7"
                      aria-label={`Distribuir a meta anual de ${kpi.name}`}
                      onClick={() => onDistribute(kpi)}
                    >
                      <Split aria-hidden />
                      Distribuir a meta anual
                    </Button>
                  ) : null}
                  <AuditLink
                    organizationId={organizationId}
                    scope={{ kpi: kpi.id, entity: 'kpi_target', name: `${kpi.code} — ${kpi.name}` }}
                    label={`Auditoria das metas de ${kpi.name}`}
                    compact
                  />
                </th>
                {periods.map((p, i) => {
                  const key = cellKeyOf(kpi.id, p);
                  const stored = saved.get(key);
                  const draft = drafts[key];
                  const shown = draft ?? (stored?.value != null ? formatValue(stored.value) : '');
                  const dirty = draft !== undefined && parseValue(draft) !== (stored?.value ?? null);
                  const bad = draft !== undefined && parseValue(draft) === 'invalid';
                  const label = `Meta de ${kpi.name} em ${targetPeriodLabel(periodType, p)}${isClosedPeriod(p) ? ' (período fechado)' : ''}`;
                  const readOnlyCell = isLocked(key);
                  const status = stored?.approvalStatus;
                  return (
                    <td key={p} className="px-1.5 py-1.5 align-top">
                      {canEdit ? (
                        <input
                          aria-label={label}
                          aria-invalid={bad || undefined}
                          inputMode="decimal"
                          value={shown}
                          disabled={locked}
                          readOnly={readOnlyCell}
                          title={readOnlyCell ? 'Meta aprovada: só quem aprova metas altera.' : undefined}
                          onChange={(e) => !readOnlyCell && onChange(key, e.target.value)}
                          className={cn(
                            'h-9 w-full min-w-24 rounded-md border border-border-strong bg-surface-raised px-2 text-right tabular-nums focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-ring/40 disabled:opacity-70',
                            readOnlyCell && 'border-dashed bg-muted text-muted-foreground',
                            dirty && 'border-brand bg-brand-soft',
                            bad && 'border-status-red-solid',
                          )}
                        />
                      ) : (
                        <p className="px-2 py-1.5 text-right tabular-nums" aria-label={label}>
                          {values[i] === null ? '—' : formatValue(values[i]!)}
                        </p>
                      )}
                      {stored && status && status !== 'APPROVED' ? (
                        <p
                          className={cn(
                            'mt-0.5 text-right text-[11px] font-semibold',
                            status === 'PENDING' ? 'text-status-yellow-fg' : 'text-status-red-fg',
                          )}
                        >
                          {status === 'PENDING' ? 'pendente' : 'rejeitada'}
                          <span className="font-normal text-muted-foreground">
                            {' '}
                            · vale {stored.approvedValue === null ? '—' : formatValue(stored.approvedValue)}
                          </span>
                        </p>
                      ) : null}
                      {stored && (stored.version > 1 || status !== 'APPROVED') ? (
                        <button
                          type="button"
                          className="mt-0.5 flex w-full items-center justify-end gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                          aria-label={`Histórico da meta de ${kpi.name} em ${targetPeriodLabel(periodType, p)}`}
                          onClick={() => onHistory(kpi, p)}
                        >
                          {readOnlyCell ? <Lock className="size-3" aria-hidden /> : null}
                          <History className="size-3" aria-hidden />
                          {stored.value === null ? `removida · v${stored.version}` : `v${stored.version}`}
                        </button>
                      ) : readOnlyCell ? (
                        <p className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
                          <Lock className="size-3" aria-hidden />
                          aprovada
                        </p>
                      ) : null}
                    </td>
                  );
                })}
                {showTotal ? (
                  <td className="px-3 py-2 text-right align-top tabular-nums">
                    {total === null ? '—' : formatValue(Math.round(total * 100) / 100)}
                    <p className="text-[11px] text-muted-foreground">
                      {KPI_AGGREGATION_LABELS[kpi.aggregation]}
                    </p>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReasonForm({
  closedLabels,
  saving,
  error,
  onSubmit,
  onCancel,
}: {
  /** Períodos fechados no lote (T-066): o texto explica o período encerrado. */
  closedLabels: string[];
  saving: boolean;
  error: unknown;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [local, setLocal] = useState<string | null>(null);
  const id = useId();
  function submit(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return setLocal('Explique o motivo da revisão.');
    setLocal(null);
    onSubmit(reason.trim());
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {closedLabels.length
          ? `Há metas de período fechado (${closedLabels.join(', ')}): a alteração fica registrada na auditoria como período encerrado, junto com esta justificativa.`
          : 'Você está mudando metas já gravadas. A versão anterior fica no histórico, junto com este motivo.'}
      </p>
      <FormField id={id} label="Justificativa" error={local ?? undefined}>
        <textarea
          {...fieldA11y(id, local ?? undefined)}
          value={reason}
          maxLength={1000}
          rows={3}
          onChange={(e) => setReason(e.target.value)}
          className="w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid"
        />
      </FormField>
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Salvar com justificativa
        </Button>
      </div>
    </form>
  );
}

type Client = ReturnType<typeof useAuth>['client'];

function DistributeForm({
  client,
  base,
  projectId,
  kpi,
  year,
  businessUnitId,
  departmentId,
  onApply,
  onCancel,
}: {
  client: Client;
  base: string;
  projectId: string;
  kpi: TargetKpi;
  year: number;
  businessUnitId?: string;
  departmentId?: string;
  onApply: (values: number[]) => void;
  onCancel: () => void;
}) {
  const [total, setTotal] = useState('');
  const [mode, setMode] = useState<DistributionModeName>('EQUAL');
  const [error, setError] = useState<string | null>(null);
  const ids = { total: useId(), mode: useId() };
  // Base da proporcional: as metas mensais do ano anterior (sazonalidade).
  const previous = useQuery({
    // Sob ['targets', projectId]: salvar ou copiar invalida também a base da proporcional.
    queryKey: ['targets', projectId, 'previous', kpi.id, year - 1, businessUnitId ?? '', departmentId ?? ''],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { year: String(year - 1), periodType: 'MONTH', kpiId: kpi.id, businessUnitId, departmentId },
        schema: targetGridSchema,
        signal,
      }),
    enabled: mode === 'PROPORTIONAL',
    retry: false,
  });
  const basis = Array.from(
    { length: 12 },
    (_, i) => previous.data?.cells.find((c) => c.period === i + 1)?.value ?? null,
  );
  const hasBasis = basis.some((v) => v !== null && v > 0);
  const negativeBasis = basis.some((v) => v !== null && v < 0);

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseValue(total);
    if (parsed === null || parsed === 'invalid') return setError('Informe a meta anual em números.');
    if (mode === 'PROPORTIONAL' && negativeBasis)
      return setError(
        `As metas mensais de ${year - 1} têm valor negativo e não servem de base proporcional; distribua igualmente.`,
      );
    if (mode === 'PROPORTIONAL' && !hasBasis)
      return setError(`Não há metas mensais em ${year - 1} para servir de base.`);
    onApply(distributeAnnual(parsed, mode, basis));
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm">
        <span className="font-semibold">{kpi.name}</span> — {year}
      </p>
      <FormField id={ids.total} label={`Meta anual (${kpi.unitLabel})`} error={error ?? undefined}>
        <Input
          {...fieldA11y(ids.total, error ?? undefined)}
          value={total}
          inputMode="decimal"
          onChange={(e) => setTotal(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.mode} label="Como distribuir">
        <NativeSelect
          id={ids.mode}
          value={mode}
          onChange={(e) => setMode(e.target.value as DistributionModeName)}
        >
          <option value="EQUAL">Igual em todos os meses</option>
          <option value="PROPORTIONAL">Proporcional às metas mensais de {year - 1}</option>
        </NativeSelect>
      </FormField>
      {mode === 'PROPORTIONAL' && previous.isPending ? (
        <LoadingState rows={1} label="Carregando as metas do ano anterior" />
      ) : null}
      {mode === 'PROPORTIONAL' && previous.isError ? (
        <ErrorState
          error={previous.error}
          onRetry={() => void previous.refetch()}
          retrying={previous.isFetching}
        />
      ) : null}
      <p className="text-xs text-muted-foreground">
        Os valores vão para as células do ano; nada é gravado até você clicar em “Salvar metas”.
      </p>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        {/* Sem a base carregada, a proporcional diria que não há metas no ano anterior. */}
        <Button type="submit" disabled={mode === 'PROPORTIONAL' && previous.isPending}>
          Preencher os meses
        </Button>
      </div>
    </form>
  );
}

function CopyForm({
  years,
  toYear,
  periodType,
  sliceName,
  onCopy,
  onCancel,
}: {
  years: number[];
  toYear: number;
  periodType: TargetPeriodTypeName;
  sliceName: string;
  onCopy: (fromYear: number, allTypes: boolean, allSlices: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [fromYear, setFromYear] = useState(
    String(years.includes(toYear - 1) ? toYear - 1 : (years[0] ?? '')),
  );
  const [allTypes, setAllTypes] = useState(false);
  const [allSlices, setAllSlices] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();
  const sliceId = useId();
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !fromYear) return;
    setBusy(true);
    setError(null);
    try {
      await onCopy(Number(fromYear), allTypes, allSlices);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      {years.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          O horizonte do projeto tem um ano só: não há de onde copiar.
        </p>
      ) : (
        <>
          <FormField id={id} label={`Copiar para ${toYear} as metas de`}>
            <NativeSelect id={id} value={fromYear} onChange={(e) => setFromYear(e.target.value)}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={sliceId} label="Recorte">
            <NativeSelect
              id={sliceId}
              value={allSlices ? 'all' : 'current'}
              onChange={(e) => setAllSlices(e.target.value === 'all')}
            >
              <option value="current">Só {sliceName}</option>
              <option value="all">Todos os recortes (geral, unidades e áreas)</option>
            </NativeSelect>
          </FormField>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-(--brand)"
              checked={allTypes}
              onChange={(e) => setAllTypes(e.target.checked)}
            />
            Todos os tipos de meta (não só {TARGET_PERIOD_TYPE_LABELS[periodType].toLowerCase()})
          </label>
          <p className="text-xs text-muted-foreground">
            Só preenche onde {toYear} ainda não tem meta; nada é sobrescrito.
          </p>
        </>
      )}
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button type="submit" loading={busy} disabled={years.length === 0}>
          Copiar metas
        </Button>
      </div>
    </form>
  );
}

function TargetHistory({
  client,
  base,
  projectId,
  kpi,
  periodType,
  year,
  period,
  businessUnitId,
  departmentId,
}: {
  client: Client;
  base: string;
  projectId: string;
  kpi: TargetKpi;
  periodType: TargetPeriodTypeName;
  year: number;
  period: number;
  businessUnitId?: string;
  departmentId?: string;
}) {
  const history = useQuery({
    // Sob ['targets', projectId]: depois de salvar, o histórico reaberto mostra a versão nova.
    queryKey: [
      'targets',
      projectId,
      'history',
      kpi.id,
      periodType,
      year,
      period,
      businessUnitId ?? '',
      departmentId ?? '',
    ],
    queryFn: ({ signal }) =>
      client.request(`${base}/history`, {
        query: {
          kpiId: kpi.id,
          periodType,
          year: String(year),
          period: String(period),
          businessUnitId,
          departmentId,
        },
        schema: targetHistorySchema,
        signal,
      }),
  });
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        <span className="font-semibold">{kpi.name}</span> — {targetPeriodLabel(periodType, period)} de {year}
      </p>
      {history.isPending ? (
        <LoadingState rows={2} label="Carregando o histórico" />
      ) : history.isError ? (
        <ErrorState
          error={history.error}
          onRetry={() => void history.refetch()}
          retrying={history.isFetching}
        />
      ) : (
        <ol aria-label="Versões da meta" className="flex flex-col gap-2">
          {history.data.items.map((v) => (
            <li key={v.id} className="rounded-md border border-border bg-surface p-3 text-sm">
              <p className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">
                  Versão {v.version}
                  {v.current ? ' (vigente)' : ''}
                </span>
                <span className="tabular-nums">
                  {v.value === null ? 'Meta removida' : `${formatValue(v.value)} ${kpi.unitLabel}`}
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {v.createdByName} · {formatDateTime(v.createdAt)}
              </p>
              {v.reason ? <p className="mt-1 text-xs">Motivo: {v.reason}</p> : null}
              <p className="mt-1 text-xs">
                <span
                  className={cn(
                    'font-semibold',
                    v.approvalStatus === 'PENDING' && 'text-status-yellow-fg',
                    v.approvalStatus === 'REJECTED' && 'text-status-red-fg',
                  )}
                >
                  {v.approvalStatus === 'PENDING' && !v.current
                    ? 'Substituída antes da decisão'
                    : TARGET_APPROVAL_LABELS[v.approvalStatus]}
                </span>
                {v.decidedByName && v.decidedAt
                  ? ` por ${v.decidedByName} em ${formatDateTime(v.decidedAt)}`
                  : ''}
              </p>
              {v.decisionComment ? <p className="mt-1 text-xs">Comentário: {v.decisionComment}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** Painel de quem aprova metas (T-063): escolher as pendentes e aprovar ou rejeitar com comentário. */
function ApprovalPanel({
  client,
  base,
  pending,
  onDecided,
}: {
  client: Client;
  base: string;
  pending: {
    data?: { total: number; items: TargetPending[] };
    isPending: boolean;
    isError: boolean;
    error: unknown;
    isFetching: boolean;
    refetch: () => unknown;
  };
  onDecided: () => Promise<unknown>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState('');
  const [local, setLocal] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<'APPROVE' | 'REJECT' | null>(null);
  const commentId = useId();
  const items = pending.data?.items ?? [];
  // A seleção vale só para o que ainda está na lista.
  const chosen = items.filter((i) => selected.has(i.id)).map((i) => i.id);
  const allChosen = items.length > 0 && chosen.length === items.length;

  async function decide(decision: 'APPROVE' | 'REJECT') {
    if (busy) return;
    setSelectionError(null);
    setLocal(null);
    if (!chosen.length) return setSelectionError('Escolha ao menos uma meta.');
    if (decision === 'REJECT' && comment.trim().length < 3)
      return setLocal('Explique o motivo da rejeição no comentário.');
    setError(null);
    setBusy(decision);
    try {
      const result = await client.request(`${base}/decisions`, {
        method: 'POST',
        body: { ids: chosen, decision, comment: comment.trim() || null },
        schema: targetDecisionResultSchema,
      });
      const n = result.approved || result.rejected;
      toast.success(
        decision === 'APPROVE'
          ? `${n} ${n === 1 ? 'meta aprovada' : 'metas aprovadas'}`
          : `${n} ${n === 1 ? 'meta rejeitada' : 'metas rejeitadas'}`,
        { description: 'Quem propôs recebe um aviso por e-mail.' },
      );
      setSelected(new Set());
      setComment('');
      await onDecided();
    } catch (err) {
      setError(err);
      // Alguém decidiu antes: a lista recarrega.
      void pending.refetch();
    } finally {
      setBusy(null);
    }
  }

  if (pending.isPending) return <LoadingState rows={3} label="Carregando as metas pendentes" />;
  if (pending.isError)
    return (
      <ErrorState
        error={pending.error}
        onRetry={() => void pending.refetch()}
        retrying={pending.isFetching}
      />
    );
  if (!items.length)
    return (
      <EmptyState
        className="py-8"
        icon={CheckCheck}
        title="Nenhuma meta pendente"
        description="As metas propostas aparecem aqui para aprovar ou rejeitar."
      />
    );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={allChosen}
            disabled={busy !== null}
            onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())}
          />
          Selecionar todas ({items.length})
        </label>
        {pending.data!.total > items.length ? (
          <span className="text-xs text-muted-foreground">
            Mostrando as {items.length} mais antigas de {pending.data!.total}.
          </span>
        ) : null}
      </div>
      <ul aria-label="Metas pendentes" className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto pr-1">
        {items.map((i) => {
          const where = i.businessUnit?.name ?? i.department?.name;
          const name = `${i.kpi.name} — ${targetPeriodLabel(i.periodType, i.period)}/${i.year}${where ? ` (${where})` : ''}`;
          return (
            <li key={i.id} className="rounded-md border border-border bg-surface p-3 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 accent-(--brand)"
                  checked={selected.has(i.id)}
                  disabled={busy !== null}
                  onChange={(e) =>
                    setSelected((all) => {
                      const next = new Set(all);
                      if (e.target.checked) next.add(i.id);
                      else next.delete(i.id);
                      return next;
                    })
                  }
                  aria-label={`Escolher ${name}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">
                    <span className="font-mono text-xs text-muted-foreground">{i.kpi.code}</span> {name}
                  </span>
                  <span className="mt-0.5 block tabular-nums">
                    Proposta:{' '}
                    {i.value === null ? 'remover a meta' : `${formatValue(i.value)} ${i.kpi.unitLabel}`}
                    <span className="text-muted-foreground">
                      {' '}
                      · em vigor:{' '}
                      {i.approvedValue === null
                        ? 'sem meta'
                        : `${formatValue(i.approvedValue)} ${i.kpi.unitLabel}`}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {i.createdByName} · {formatDateTime(i.createdAt)} · versão {i.version}
                  </span>
                  {i.reason ? <span className="mt-0.5 block text-xs">Motivo: {i.reason}</span> : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {selectionError ? <FormAlert tone="error">{selectionError}</FormAlert> : null}
      <FormField id={commentId} label="Comentário (obrigatório para rejeitar)" error={local ?? undefined}>
        <textarea
          {...fieldA11y(commentId, local ?? undefined)}
          value={comment}
          maxLength={1000}
          rows={2}
          disabled={busy !== null}
          onChange={(e) => setComment(e.target.value)}
          className="w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid"
        />
      </FormField>
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          loading={busy === 'REJECT'}
          disabled={busy !== null}
          onClick={() => void decide('REJECT')}
        >
          Rejeitar selecionadas ({chosen.length})
        </Button>
        <Button loading={busy === 'APPROVE'} disabled={busy !== null} onClick={() => void decide('APPROVE')}>
          Aprovar selecionadas ({chosen.length})
        </Button>
      </div>
    </div>
  );
}
